import React, { useState, useRef, useEffect } from 'react';
import { 
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  Monitor,
  MonitorOff,
  MessageSquare,
  FileText,
  Save,
  Download,
  Clock,
  DollarSign,
  Shield,
  AlertTriangle,
  Users,
  Settings,
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  Circle,
  Square,
  Send,
  Paperclip,
  Calendar,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { useLegalVideoConsultation } from '../../hooks/useLegalVideoConsultation';

interface ConsultationProps {
  clientName: string;
  caseType: string;
  attorneyName: string;
  firmName: string;
  consultationId: string;
  onEndConsultation?: () => void;
  onSaveNotes?: (notes: string) => void;
  onScheduleFollowUp?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'attorney' | 'client' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

export const VideoConsultation: React.FC<ConsultationProps> = ({
  clientName,
  caseType,
  attorneyName,
  firmName,
  consultationId,
  onEndConsultation,
  onSaveNotes,
  onScheduleFollowUp
}) => {
  // Use the legal video consultation hook
  const {
    room,
    participant,
    status,
    isConnected,
    isLoading,
    error,
    videoState,
    consultationTime,
    recordingConsent,
    formattedTime,
    billableAmount,
    createConsultation,
    joinAsAttorney,
    joinAsClient,
    toggleVideo,
    toggleAudio,
    toggleScreenShare,
    toggleRecording,
    updateRecordingConsent,
    endConsultation
  } = useLegalVideoConsultation({
    onConsultationEnd: (billingSummary) => {
      console.log('Consultation ended with billing:', billingSummary);
      onEndConsultation?.();
    },
    onError: (error) => {
      console.error('Video consultation error:', error);
    }
  });

  // UI states
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);

  // Consultation data
  const [caseNotes, setCaseNotes] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Consultation started. Attorney-client privilege is now in effect.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');

  // Legal compliance
  const [attorneyClientNotified, setAttorneyClientNotified] = useState(true);

  // Refs
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize consultation when component mounts
  useEffect(() => {
    const initializeConsultation = async () => {
      try {
        // Check URL params to see if we have a room ID
        const urlParams = new URLSearchParams(window.location.search);
        const existingRoomId = urlParams.get('roomId');
        const userType = urlParams.get('type');

        console.log('[VideoConsultation] Initializing with:', {
          existingRoomId,
          userType,
          clientName,
          attorneyName
        });

        if (existingRoomId) {
          // Join existing room
          if (userType === 'legal_attorney') {
            await joinAsAttorney(existingRoomId, attorneyName);
          } else {
            await joinAsClient(existingRoomId, clientName);
          }
        } else {
          // Create new consultation room
          const roomId = await createConsultation({
            clientName,
            attorneyName,
            firmName,
            caseType,
            caseId: consultationId,
            urgency: 'medium',
            billableRate: 350,
            estimatedDuration: 60,
            recordingRequested: false
          });

          // Auto-join as attorney (since this is the consultation interface)
          await joinAsAttorney(roomId, attorneyName);
        }
      } catch (error) {
        console.error('Failed to initialize consultation:', error);
      }
    };

    // Only initialize once when the component first mounts
    initializeConsultation();
  }, []); // Empty dependency array to run only once

  // Initialize local media stream
  useEffect(() => {
    const initializeMedia = async () => {
      try {
        if (videoState.localVideo || videoState.localAudio) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: videoState.localVideo,
            audio: videoState.localAudio
          });
          
          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
          
          console.log('[VideoConsultation] Local media initialized');
        }
      } catch (error) {
        console.error('Failed to initialize local media:', error);
      }
    };

    initializeMedia();
    
    // Cleanup function
    return () => {
      if (localVideoRef.current?.srcObject) {
        const stream = localVideoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoState.localVideo, videoState.localAudio]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Remove old functions - now using hook methods

  const handleRecordingToggle = async () => {
    try {
      await toggleRecording();
      
      // Add system message
      const recordingMessage: ChatMessage = {
        id: Date.now().toString(),
        sender: 'system',
        message: videoState.recording 
          ? 'Recording stopped. Recording saved to case file.'
          : 'Recording started with client consent.',
        timestamp: new Date(),
        type: 'system'
      };
      setChatMessages(prev => [...prev, recordingMessage]);
    } catch (error) {
      console.error('Recording toggle failed:', error);
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: 'attorney',
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  const handleEndConsultation = async () => {
    if (window.confirm('Are you sure you want to end this consultation?')) {
      try {
        // Save final notes
        onSaveNotes?.(caseNotes);
        
        // End the video consultation
        await endConsultation();
        
        // Navigate back to attorney dashboard
        window.history.pushState({}, '', '/templates/legal/attorney');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (error) {
        console.error('Failed to end consultation:', error);
      }
    }
  };

  const quickNotes = [
    'Client seeks representation for personal injury claim',
    'Statute of limitations: ',
    'Damages discussed: ',
    'Next steps: Document review and case evaluation',
    'Follow-up consultation scheduled',
    'Retainer agreement to be sent'
  ];

  return (
    <div className={`h-screen bg-gray-900 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold">{clientName}</h2>
            <p className="text-sm text-gray-300">{caseType} • ID: {consultationId}</p>
          </div>
          {attorneyClientNotified && (
            <div className="bg-green-900 text-green-200 px-3 py-1 rounded-full text-xs flex items-center">
              <Shield size={12} className="mr-1" />
              Privileged Communication
            </div>
          )}
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer and Billing */}
          <div className="text-right">
            <div className="flex items-center text-lg font-mono">
              <Clock size={16} className="mr-2" />
              {formattedTime}
            </div>
            <div className="flex items-center text-sm text-gray-300">
              <DollarSign size={12} className="mr-1" />
              ${billableAmount.toFixed(2)}
            </div>
          </div>

          {/* Recording Status */}
          {videoState.recording && (
            <div className="bg-red-900 text-red-200 px-3 py-1 rounded-full text-xs flex items-center animate-pulse">
              <Circle size={8} className="mr-2 fill-current" />
              Recording
            </div>
          )}

          {/* Connection Status */}
          {isLoading && (
            <div className="bg-yellow-900 text-yellow-200 px-3 py-1 rounded-full text-xs flex items-center">
              <Circle size={8} className="mr-2 fill-current animate-spin" />
              Connecting...
            </div>
          )}

          {error && (
            <div className="bg-red-900 text-red-200 px-3 py-1 rounded-full text-xs flex items-center">
              <AlertTriangle size={12} className="mr-2" />
              Connection Error
            </div>
          )}

          {/* Settings */}
          <button className="p-2 hover:bg-gray-700 rounded-lg">
            <Settings size={18} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area */}
        <div className="flex-1 relative bg-black">
          {/* Remote Video (Client) */}
          <video
            ref={remoteVideoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
          />
          
          {/* Local Video (Attorney) - Picture in Picture */}
          <div className="absolute top-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600">
            <video
              ref={localVideoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            {!videoState.localVideo && (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <VideoOff className="text-gray-400" size={24} />
              </div>
            )}
          </div>

          {/* Client Name Overlay */}
          <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-lg">
            {clientName}
          </div>

          {/* Screen Share Indicator */}
          {videoState.screenShare && (
            <div className="absolute top-4 left-4 bg-blue-900 text-blue-200 px-3 py-1 rounded-lg text-sm flex items-center">
              <Monitor size={14} className="mr-2" />
              Screen Sharing
            </div>
          )}

          {/* Controls Overlay */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-6 py-3 flex items-center space-x-4">
              {/* Audio */}
              <button
                onClick={toggleAudio}
                className={`p-3 rounded-full transition-colors ${
                  videoState.localAudio 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localAudio ? <Mic size={20} /> : <MicOff size={20} />}
              </button>

              {/* Video */}
              <button
                onClick={toggleVideo}
                className={`p-3 rounded-full transition-colors ${
                  videoState.localVideo 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {videoState.localVideo ? <Video size={20} /> : <VideoOff size={20} />}
              </button>

              {/* Screen Share */}
              <button
                onClick={toggleScreenShare}
                className={`p-3 rounded-full transition-colors ${
                  videoState.screenShare 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                {videoState.screenShare ? <MonitorOff size={20} /> : <Monitor size={20} />}
              </button>

              {/* Recording */}
              <button
                onClick={handleRecordingToggle}
                className={`p-3 rounded-full transition-colors ${
                  videoState.recording 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                title={videoState.recording ? 'Stop Recording' : 'Start Recording'}
              >
                {videoState.recording ? <Square size={20} /> : <Circle size={20} />}
              </button>

              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-3 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={20} />
              </button>

              {/* End Call */}
              <button
                onClick={handleEndConsultation}
                className="p-3 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <Phone size={20} />
              </button>
            </div>
          </div>

          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-lg"
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>

        {/* Side Panel */}
        <div className={`bg-white flex flex-col transition-all duration-300 ${showNotes || showChat ? 'w-96' : 'w-0 overflow-hidden'}`}>
          {/* Panel Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    showNotes 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FileText size={14} className="inline mr-1" />
                  Notes
                </button>
                <button
                  onClick={() => setShowChat(!showChat)}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    showChat 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <MessageSquare size={14} className="inline mr-1" />
                  Chat
                </button>
              </div>
              <button
                onClick={() => {
                  setShowNotes(false);
                  setShowChat(false);
                }}
                className="p-1 hover:bg-gray-200 rounded"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Case Notes Panel */}
          {showNotes && (
            <div className="flex-1 flex flex-col p-4 border-r border-gray-200">
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Case Notes</h3>
                <div className="text-sm text-gray-600 mb-3">
                  <p>Client: {clientName}</p>
                  <p>Case Type: {caseType}</p>
                  <p>Date: {new Date().toLocaleDateString()}</p>
                </div>
              </div>

              {/* Quick Note Buttons */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Notes
                </label>
                <div className="space-y-1">
                  {quickNotes.map((note, index) => (
                    <button
                      key={index}
                      onClick={() => setCaseNotes(prev => prev + (prev ? '\n' : '') + '• ' + note)}
                      className="w-full text-left text-xs p-2 bg-gray-50 hover:bg-gray-100 rounded border text-gray-700"
                    >
                      {note}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes Textarea */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consultation Notes
                </label>
                <textarea
                  value={caseNotes}
                  onChange={(e) => setCaseNotes(e.target.value)}
                  className="w-full h-full resize-none border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Document case details, client statements, legal issues, next steps..."
                />
              </div>

              {/* Notes Actions */}
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => onSaveNotes?.(caseNotes)}
                  className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center"
                >
                  <Save size={14} className="mr-1" />
                  Save
                </button>
                <button
                  onClick={onScheduleFollowUp}
                  className="flex-1 bg-gray-600 text-white py-2 px-3 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center justify-center"
                >
                  <Calendar size={14} className="mr-1" />
                  Follow-up
                </button>
              </div>
            </div>
          )}

          {/* Chat Panel */}
          {showChat && (
            <div className="flex-1 flex flex-col">
              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${
                      msg.sender === 'system' 
                        ? 'text-center' 
                        : msg.sender === 'attorney' 
                          ? 'ml-auto' 
                          : 'mr-auto'
                    }`}
                  >
                    {msg.sender === 'system' ? (
                      <div className="bg-yellow-50 text-yellow-800 text-xs px-3 py-2 rounded-lg border border-yellow-200">
                        {msg.message}
                      </div>
                    ) : (
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                          msg.sender === 'attorney'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-900'
                        }`}
                      >
                        <p>{msg.message}</p>
                        <p className="text-xs opacity-75 mt-1">
                          {msg.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="border-t border-gray-200 p-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Type a message..."
                  />
                  <button
                    onClick={sendMessage}
                    className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legal Compliance Modal */}
      {!recordingConsent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-amber-500 mr-3" size={24} />
              <h3 className="text-lg font-semibold">Recording Consent Required</h3>
            </div>
            <p className="text-gray-600 mb-6">
              To record this consultation, both parties must provide explicit consent. 
              This recording will be subject to attorney-client privilege.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => updateRecordingConsent(true)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Grant Consent
              </button>
              <button
                onClick={() => updateRecordingConsent(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Proceed Without Recording
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};